import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { DisplayProductDataService } from '../display-product-data.service';
import { ControllerItem, PackagingItem } from '../controllers/controller.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CopyButtonComponent } from '../copy-button/copy-button.component';
import { v4 } from 'uuid';
import { CONTROLLER_ADDITIONAL_FIELDS, CONTROLLER_ITEMS, CONTROLLER_OPTIONAL_ITEMS } from '../pump-data.model';
import { checkControllerName } from '../names.validator';
import { UserLocalStorage } from '../../login/user-local-store';
import { Router } from '@angular/router';
import { delay, map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { PackagingState } from '../store/packaging/packaging.reducer';
import { selectPackaging } from '../store/packaging/packaging.selector';


@Component({
  selector: 'app-controller-new',
  templateUrl: './controller-new.component.html',
  styleUrls: ['./controller-new.component.scss'],
  standalone: false
})
export class ControllerNewComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];

  controllerForm!: UntypedFormGroup;
  readonly additionalFields = CONTROLLER_ADDITIONAL_FIELDS;
  controller!: Observable<ControllerItem>;

  userLocalStorage = new UserLocalStorage();
  copiedItem!: string;
  showLoading = false;
  isDirty = false;

  packagingList: PackagingItem[] = [];
  originalControllerFormValue: any;

  constructor(
    private store: Store,
    private router: Router,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private displayProductData: DisplayProductDataService
  ) { }

  ngOnInit() {

    const fieldDefaults: Record<string, any> = {
      d: 0, d1: 0, d2: 0, e: 0, e1: 0,
      h: 0, h1: 0, h2: 0, h3: 0,
      w: 0, w1: 0, w2: 0, w3: 0,
      efficiency: 0, i_factor: 0, i_offset: 0, u_factor: 0,
      max_temp: 0, min_temp: 0,
      max_i: 0, max_freq: 0, max_p: 0, max_voc: 0, min_cable_size: 0,
      op_voc: 0, output_rated_voc: 0,
      net_weight: 0, gross_weight: 0, economical_value_for_sort_order: 0,
      max_isc: 0, max_output_voc: '', max_output_i: 0, max_output_p: 0, min_vmp: 0,
      name: '', item: '', uuid: '',
      picture: '', picture_url: '', drawing: '', drawing_url: '',
      ingress_protection_code: '', description: '', freq_range: '0-0',
    };
    const controllerFormValue: any = {};
    CONTROLLER_ITEMS.forEach(name => {
      const defaultValue = name in fieldDefaults ? fieldDefaults[name] : null;
      controllerFormValue[`${name}`] = [defaultValue, !CONTROLLER_OPTIONAL_ITEMS.includes(name) && !name.includes('picture') && !name.includes('drawing') ? Validators.required : []];
    });
    controllerFormValue.name = ['', [Validators.required, checkControllerName.bind(this)]];
    this.controllerForm = this.fb.group(controllerFormValue);
    this.originalControllerFormValue = this.controllerForm.getRawValue();
    this.setupFormListeners();

    const getPackagingList = this.store.pipe(
      select(selectPackaging),
      map((data: PackagingState) => data.data)
    ).subscribe((data: PackagingItem[]) => {
      this.packagingList = [...data].sort((a, b) => Number(a.subType.L) - Number(b.subType.L));
    });
    

    this.subscriptions.push(getPackagingList);
  }

  openCopyDialog(type: string) {
    const dialogRef = this.dialog.open(CopyButtonComponent, {
      width: '1000px',
      maxWidth: '96vw',
      data: type,
      minHeight: '60vh',
      maxHeight: '60vh',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showLoading = true;
        this.copyItem(result);
      }
    });
  }

  copyItem(controllerUuid: string) {

    this.displayProductData.getControllerItem(controllerUuid).subscribe((controller: ControllerItem) => {

      this.showLoading = false;
      this.copiedItem = controller.name;

      const controllerFormValue: any = {};
      CONTROLLER_ITEMS.forEach(name => {
        controllerFormValue[`${name}`] = (controller as any)[`${name}`];
      });
      controllerFormValue.name = '';
      this.controllerForm.patchValue(controllerFormValue);
      
    });
    
  }

  private setupFormListeners() {
    this.controllerForm.valueChanges.subscribe(value => {
      // your existing logic unchanged
      this.isDirty =
        JSON.stringify(this.originalControllerFormValue) !== JSON.stringify(value);
    });
  }

  onSubmit(data: any) {
    console.log('Form values before submit:', {
      picture: this.controllerForm.get('picture')?.value,
      picture_url: this.controllerForm.get('picture_url')?.value,
      drawing: this.controllerForm.get('drawing')?.value,
      drawing_url: this.controllerForm.get('drawing_url')?.value
    });
    console.log('Data to be submitted:', data);

    const user = this.userLocalStorage.loadUser();
    data.author = user!.user.username;
    data.uuid = v4();

    if (this.controllerForm.value.packaging.subType) {
      const packagingValue: any = {};
      packagingValue.name = data.packaging.uuid;
      packagingValue.w = data.packaging.subType.W;
      packagingValue.h = data.packaging.subType.H;
      packagingValue.l = data.packaging.subType.L;
      packagingValue.pack_weight = data.packaging.weight;
      data.packaging = packagingValue;
    } else {
      const result = this.packagingList.filter(item => item.name!.includes(data.packaging));
      const packaging: any = {};
      packaging.name = result[0].uuid;
      packaging.w = `${result[0].subType.W}`;
      packaging.h = `${result[0].subType.H}`;
      packaging.l = `${result[0].subType.L}`;
      packaging.pack_weight = `${result[0].weight}`;
      data.packaging = packaging;
    }
    if (this.controllerForm.get('drawing_url')!.value === '' ||
      this.controllerForm.get('drawing_url')!.value === null || this.controllerForm.get('drawing_url')!.value === undefined) {
      this.controllerForm.get('drawing_url')!.setValue(data.drawing_url);
    }

    this.displayProductData.createNewController(data).subscribe(item => {
      this.snackBar
        .open(item, '', { duration: 1500, panelClass: 'hintMsg' })
        .afterDismissed()
        .pipe(delay(0))
        .subscribe(() => {
          this.displayProductData.reloadControllerData();
          this.router.navigate(['/data/controller', data.uuid]);
        });
    });
  }

  onDrawingSelect(event: any) {
    console.log('Drawing selection changed:', event);
    const drawingValue = event.value;
    const drawingName = this.extractNameFromUrl(drawingValue);
    this.controllerForm.get('drawing_url')!.setValue(drawingValue);
    this.controllerForm.get('drawing')!.setValue(drawingName);
    this.controllerForm.patchValue({
      drawing: drawingName,
      drawing_url: drawingValue
    });
  }

  onPictureSelect($event: any) {
    console.log('Picture selection changed:', $event);
    const pictureValue = $event.value;
    const pictureName = this.extractNameFromUrl(pictureValue);
    console.log('Picture - name:', pictureName, 'url:', pictureValue);
    console.log('Before patch - picture:', this.controllerForm.get('picture')?.value, 'drawing:', this.controllerForm.get('drawing')?.value);
    this.controllerForm.get('picture_url')!.setValue(pictureValue);
    this.controllerForm.get('picture')!.setValue(pictureName);
    this.controllerForm.patchValue({
      picture: pictureName,
      picture_url: pictureValue
    });
    console.log('After patch - picture:', this.controllerForm.get('picture')?.value, 'drawing:', this.controllerForm.get('drawing')?.value);
  }

  private extractNameFromUrl(value: string): string {
    if (!value) return '';
    // If it's a URL, extract the name
    if (value.includes('http') || value.includes('s3')) {
      const nameMatch = value.match(/\/([^\/]+)\.[^\/]+$/);
      return nameMatch ? nameMatch[1] : value;
    }
    // Otherwise it's already a name
    return value;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}

